import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
    Breadcrumbs, Button, CloseButton, FieldError, Input, Label, ListBox,
    ListBoxItem, Modal, SearchField, Select, TextArea, TextField
} from '@heroui/react';

import ResourceItem from '../resources/ResourceItem';
import { SkeletonCardColumns } from '../../common/Skeleton';
import { EmptyResult } from '../../common/LoadMore';
import Pagination from '../../common/Pagination';
import Notice from '../../common/Notice';
import FieldWithAction from '../../common/FieldWithAction';
import UserBadge from '../../common/UserBadge';
import ExportJsonLd from '../../common/ExportJsonLd';
import ViewTab from '../../common/ViewTab';
import ActiveFilters from '../../common/ActiveFilters';
import CollectionJudgements from '../judgements/CollectionJudgements';
import CollectionTasks from '../tasks/CollectionTasks';
import CollectionActivity from '../activity/CollectionActivity';
import useValidation from '../../common/useValidation';
import { useBrowseState, useSearchTerm } from '../../common/useBrowseState';
import FundingFields, { EMPTY_FUNDING, fundingPayloadOf } from '../../common/FundingFields';
import DiscardedCuration from '../../common/DiscardedCuration';
import SuggestionsField from '../../common/SuggestionsField';
import { getTopics } from '../../../services/topics.service';
import FundingFilters from '../../common/FundingFilters';
// Τα chips ορατότητας, αρχειοθέτησης και θέματος αποδίδονται τώρα από την κεφαλίδα:
// ζουν στην πάνω μπάρα μαζί με τον τίτλο, αντί να επαναλαμβάνονται μέσα στη σελίδα.
import { RoleChip, TagChip } from '../../common/StatusChip';
import userService from '../../../services/auth.service';
import { getUser } from '../../../services/session';
import { fieldErrorsOf } from '../../../services/apiClient';
import * as check from '../../../services/validation';
import { isFiltering, hasStructuredFilters, resourceDialogPlan, SORTS, TABS } from '../../../services/browseState';
import { replacementDraftOf } from '../resources/resourceEdit';
import { messageOf, LABELS, RESOURCE_CREATED, SORT_OPTIONS, P1_5_TEXT, METADATA_SOURCES, COLLECTION_REMOVAL, SELECT_PLACEHOLDER, TOPICS } from '../../../i18n/el';

// Σελίδα θεματικής συλλογής: οι πόροι της, η φόρμα καταχώρησης (ΛΑ-08), τα μέλη
// (ΛΑ-04) και η αρχειοθέτηση (ΛΑ-07). Τα δικαιώματα ελέγχονται πάντοτε στο API·
// εδώ απλώς δεν εμφανίζονται λειτουργίες που ο χρήστης δεν μπορεί να εκτελέσει.

// Η **εικόνα** έλειπε από τη φόρμα καταχώρησης: πόρος καταχωρημένος από τη
// διαδικτυακή εφαρμογή δεν μπορούσε ΠΟΤΕ να έχει εικόνα, παρά μόνο αν προστεθεί
// εκ των υστέρων από την επεξεργασία. Η ΛΑ-10 ζητά έλεγχο και συμπλήρωση
// μεταδεδομένων **και πριν** την καταχώρηση, και η επεξεργασία πρόσφερε τέσσερα
// πεδία ενώ η καταχώρηση τρία.
const EMPTY_RESOURCE = { url: '', title: '', description: '', image: '', tags: '', funding: EMPTY_FUNDING };

// ΛΑ-05: τα ίδια τέσσερα στοιχεία που ορίζει η δημιουργία, με τα **ίδια λόγια**.
// Οι δύο κατάλογοι είναι σκόπιμα ταυτόσημοι με τους αντίστοιχους της οθόνης
// καταλόγου: δύο διατυπώσεις της ίδιας σύμβασης σε δύο οθόνες είναι το μοτίβο που
// απαγορεύει η ΜΛΑ-02.
const VISIBILITIES = [
    { id: 'private', label: 'Ιδιωτική, ορατή μόνο στα μέλη' },
    { id: 'public', label: 'Δημόσια, ορατή σε όλους' }
];

// ΛΑ-04: οι δύο βαθμίδες του Πίνακα 1 εντός συλλογής. Ο `admin` είναι **καθολικός**
// και δεν αποδίδεται εδώ — δεν είναι ιδιότητα της συμμετοχής.
const MEMBER_ROLES = [
    { id: 'member', label: LABELS.role.member },
    { id: 'coordinator', label: LABELS.role.coordinator }
];

/**
 * Η **προέλευση** ενός αντλημένου πεδίου, δίπλα στην ετικέτα του (ΛΑ-09).
 *
 * Είναι το ορατό τεκμήριο της απαίτησης, και το ίδιο που δείχνει η επέκταση με
 * τα ίδια λόγια: ο χρήστης βλέπει τι **διάβασε** το σύστημα από τη σελίδα και τι
 * **μάντεψε**, οπότε ξέρει ποιο πεδίο αξίζει να ελέγξει. Χωρίς αυτό, η αυτόματη
 * συμπλήρωση είναι αδιαφανής: τρία γεμάτα πεδία, χωρίς ένδειξη ποιο είναι
 * δήλωση του ιστότοπου και ποιο εικασία από το όνομα του υπολογιστή.
 */
function SourceHint({ source }) {
    if (!source) return null;

    return (
        <span className="ml-2 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-normal text-accent-soft-foreground">
            {METADATA_SOURCES[source] ?? source}
        </span>
    );
}

/** Το εικονίδιο κάθε καρτέλας. Κλειδί προς κλειδί με το `TABS.collection`. */
const TAB_ICONS = {
    resources: 'fa-solid fa-link',
    tasks: 'fa-solid fa-list-check',
    judgements: 'fa-solid fa-star-half-stroke',
    activity: 'fa-solid fa-wave-square',
    members: 'fa-solid fa-users'
};

/**
 * Μία καρτέλα, πάνω στο **υπάρχον** `ViewTab`.
 *
 * Χωριστό συστατικό ώστε ο χειριστής της να είναι **ονομασμένη συνάρτηση με την
 * καρτέλα στο περιβάλλον της**: ενσωματωμένο βέλος μέσα σε χαρακτηριστικό JSX
 * κόβει τη σάρωση του στατικού ελέγχου καλωδίωσης στο πρώτο `>`, που το βέλος
 * περιέχει.
 */
function CollectionTab({ tab, active, onSelect }) {
    function handlePress() {
        onSelect(tab);
    }

    return (
        <ViewTab icon={TAB_ICONS[tab]} isActive={active === tab} onPress={handlePress}>
            {P1_5_TEXT.tabs[tab]}
        </ViewTab>
    );
}

/** Μία ετικέτα ως φίλτρο με ένα πάτημα (ΛΑ-15), με ονομασμένο χειριστή. */
function TagFilterButton({ tag, isActive, onToggle }) {
    function handleClick() {
        onToggle(tag);
    }

    return (
        <button
            type="button"
            aria-pressed={isActive}
            onClick={handleClick}
            className="rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
        >
            <TagChip appearance={isActive ? 'solid' : 'outline'}>{tag}</TagChip>
        </button>
    );
}

/** ΛΑ-04: ο επιλογέας ρόλου ενός μέλους, με ονομασμένο χειριστή. */
function MemberRoleSelect({ member, onChange }) {
    function handleChange(key) {
        onChange(idOf(member), String(key));
    }

    return (
        <Select placeholder={SELECT_PLACEHOLDER}
            selectedKey={member.role}
            onSelectionChange={handleChange}
            aria-label={`Ρόλος του μέλους ${member.user?.username ?? ''}`}
            className="w-36 shrink-0"
        >
            <Select.Trigger>
                <Select.Value />
                <Select.Indicator />
            </Select.Trigger>
            <Select.Popover>
                <ListBox items={MEMBER_ROLES}>
                    {(item) => <ListBoxItem id={item.id}>{item.label}</ListBoxItem>}
                </ListBox>
            </Select.Popover>
        </Select>
    );
}

/** ΛΑ-04: η αφαίρεση μέλους, με ονομασμένο χειριστή. */
function MemberRemoveButton({ member, onRemove }) {
    function handlePress() {
        onRemove(idOf(member));
    }

    return (
        <CloseButton
            aria-label={`Αφαίρεση του μέλους ${member.user?.username ?? ''}`}
            onPress={handlePress}
        />
    );
}

/** Ο ρόλος του συνδεδεμένου χρήστη μέσα στη συλλογή, κατά τον Πίνακα 1. */
function roleIn(collection) {
    const user = getUser();
    if (!user) return 'guest';
    if (user.isAdmin) return 'admin';

    const membership = collection?.members?.find(
        (member) => (member.user?.id ?? member.user) === user.id);

    return membership ? membership.role : 'guest';
}

const idOf = (member) => member.user?.id ?? member.user;

export default function CollectionPage({ onTitle, onStatus }) {
    const { id: collectionId } = useParams();
    const navigate = useNavigate();

    const [collection, setCollection] = useState(null);
    const [resources, setResources] = useState([]);
    // Η σελιδοποίηση του API. Χωρίς αυτή, μια συλλογή με 500 πόρους εμφάνιζε 20.
    const [meta, setMeta] = useState(null);
    const [error, setError] = useState('');

    // Αναζήτηση, ταξινόμηση, σελίδα και **ο ανοιχτός πόρος**, όλα στη διεύθυνση
    // (P1-5). Ένας μηχανισμός για τα τέσσερα: ο μόνιμος σύνδεσμος δεν είναι
    // δεύτερη υλοποίηση αλλά μία ακόμη παράμετρος της ίδιας κατάστασης.
    const { state, query, setFilter, setPage, setSort, setTab, openResource, closeResource } =
        useBrowseState({ sorts: SORTS.resources, tabs: TABS.collection });

    // **Απόφαση D30: η περιοχή των φίλτρων συμπτύσσεται.**
    //
    // Η κατάσταση είναι **τοπική** και όχι στη διεύθυνση, γιατί είναι εμφάνιση
    // και όχι συμπεριφορά: τα ίδια τα φίλτρα εξακολουθούν να ζουν στη διεύθυνση,
    // αμετάβλητα. Ο σύνδεσμος που μοιράζεται κανείς περιγράφει **τι φιλτράρει**
    // και όχι πόσο ανοιχτό ήταν το πλαίσιο όταν το έγραψε.
    //
    // Ανοίγει μόνη της όταν η διεύθυνση φέρει ήδη δομημένα φίλτρα: αλλιώς ο
    // παραλήπτης ενός φιλτραρισμένου συνδέσμου θα έβλεπε μικρό αποτέλεσμα χωρίς
    // τα πεδία που το παράγουν.
    const [areFiltersOpen, setFiltersOpen] = useState(() => hasStructuredFilters(state));

    // Σε ποια όψη προσγειώνεται ο διάλογος του πόρου. Γράφεται από την κάρτα που
    // τον ανοίγει και διαβάζεται μία φορά, όταν αυτός προσαρτάται.
    const [landingPane, setLandingPane] = useState('resource');
    const toggleFilters = () => setFiltersOpen((open) => !open);
    const [term, setTerm] = useSearchTerm(
        state.q,
        useCallback((value) => setFilter({ q: value }, { replace: true }), [setFilter])
    );

    // Σκέλος β: ο πόρος του συνδέσμου όταν **δεν** βρίσκεται στη φορτωμένη σελίδα.
    const [linkedResource, setLinkedResource] = useState(null);
    const [linkNotice, setLinkNotice] = useState('');

    const [form, setForm] = useState(EMPTY_RESOURCE);
    const [isSaving, setSaving] = useState(false);
    // Απόφαση D12: η άντληση μεταδεδομένων και η **προέλευση** κάθε πεδίου.
    const [isFetchingMeta, setFetchingMeta] = useState(false);
    const [metaNotice, setMetaNotice] = useState(null);
    const [metaSources, setMetaSources] = useState(null);
    const [formError, setFormError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [duplicateId, setDuplicateId] = useState('');
    const [outcome, setOutcome] = useState(null);
    // Απόφαση D10, σκέλος β. Κρατά τον πόρο του οποίου η διεύθυνση διορθώνεται.
    // Η αποσύνδεσή του γίνεται ΜΟΝΟ μετά από επιτυχή δημιουργία του νέου: αν ο
    // χρήστης εγκαταλείψει τη φόρμα, δεν αφαιρείται τίποτα.
    const [replacing, setReplacing] = useState(null);

    // **P1-5β.** Ό,τι έστειλε ο χρήστης και δεν αποθηκεύτηκε στην προσάρτηση,
    // μαζί με τον πόρο στον οποίο αφορά. Ζει **όσο το μήνυμα**: δεν αποθηκεύεται
    // πουθενά, δεν ανακτάται, και χάνεται με το κλείσιμο — όπως και σήμερα, με τη
    // διαφορά ότι ο χρήστης πρόλαβε να το δει.
    const [discarded, setDiscarded] = useState({ entries: [], resourceId: null, values: null });

    // Το πρόχειρο που περιμένει τον διάλογο του πόρου. Καταναλώνεται **μία φορά**,
    // από τον πόρο που το ζήτησε: αν έμενε, θα ξαναγέμιζε τη φόρμα κάθε φορά που
    // ο χρήστης ανοίγει τον ίδιο πόρο, με τιμές που εκείνος έχει ήδη απορρίψει.
    const [forwarded, setForwarded] = useState(null);

    const [newMember, setNewMember] = useState('');
    const [memberError, setMemberError] = useState('');

    // ΛΑ-05: η φόρμα επεξεργασίας της συλλογής. Ξεχωριστή κατάσταση από τη φόρμα
    // πόρου: τα δύο παράθυρα μπορούν να ανοίξουν το ένα μετά το άλλο, και κοινό
    // `formError` θα μετέφερε το σφάλμα του ενός στο άλλο.
    const [isEditOpen, setEditOpen] = useState(false);
    // **D47:** το ελεγχόμενο λεξιλόγιο, φορτωμένο **μία φορά** με τη σελίδα και όχι
    // με το άνοιγμα του διαλόγου — κατάλογος που φτάνει μισό δευτερόλεπτο μετά
    // διαβάζεται ως «δεν υπάρχουν θέματα». Η αποτυχία **καταπίνεται**: η φόρμα το
    // λέει με σύνδεσμο, αντί να στείλει τον χρήστη να διορθώσει τη συλλογή του.
    const [topicNames, setTopicNames] = useState([]);

    useEffect(() => {
        getTopics().then(
            (data) => setTopicNames((data ?? []).map((topic) => topic.name)),
            () => setTopicNames([])
        );
    }, []);

    const [editForm, setEditForm] = useState(null);
    const [editError, setEditError] = useState('');
    const [editFieldErrors, setEditFieldErrors] = useState({});
    const [isSavingEdit, setSavingEdit] = useState(false);

    const [isResourceOpen, setResourceOpen] = useState(false);
    const [isDeleteOpen, setDeleteOpen] = useState(false);
    // ΜΛΑ-03: τα πλήθη της διαγραφής. **Τρεις καταστάσεις και όχι δύο** — δεν
    // μετρήθηκαν ακόμη (`null`), μετρήθηκαν, ή **η μέτρηση απέτυχε**. Η τρίτη δεν
    // επιτρέπεται να καταρρεύσει στην πρώτη ή σε μηδενικά: «δεν ξέρω τι θα χαθεί»
    // είναι διαφορετική πληροφορία από «δεν χάνεται τίποτα».
    const [removal, setRemoval] = useState(null);
    const [removalFailed, setRemovalFailed] = useState(false);
    const [isDeleting, setDeleting] = useState(false);

    const validation = useValidation();
    // Δεύτερο στιγμιότυπο, όχι κοινό: τα δύο παράθυρα έχουν πεδίο με το **ίδιο
    // όνομα** (`title`), οπότε ένα κοινό στιγμιότυπο θα μετέφερε το «αγγιγμένο»
    // του ενός στο άλλο και το μήνυμα θα εμφανιζόταν πριν γράψει ο χρήστης.
    const editValidation = useValidation();

    /**
     * Φορτώνει τη συλλογή και **τη σελίδα πόρων που λέει η διεύθυνση**.
     *
     * Η υπογραφή δεν αλλάζει: εξακολουθεί να καλείται χωρίς ορίσματα, από κάθε
     * ενέργεια που μεταβάλλει την κάρτα, και από τα σημεία προσάρτησης των P1-3
     * και P1-4. Ό,τι άλλαξε είναι ότι διαβάζει το ενεργό ερώτημα αντί να ζητά
     * πάντα την πρώτη σελίδα χωρίς φίλτρα.
     */
    const fetchAll = useCallback(() => {
        Promise.all([
            userService.getCollection(collectionId),
            userService.getCollectionResources(collectionId, { query })
        ]).then(
            ([loaded, page]) => {
                onTitle(loaded.title);
                onStatus({
                    visibility: loaded.visibility,
                    archived: loaded.archived,
                    topic: loaded.topic
                });
                setCollection(loaded);
                setResources(page.data);
                setMeta(page.meta);
                setError('');
            },
            (apiError) => setError(messageOf(apiError))
        );
    }, [collectionId, onTitle, onStatus, query]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    /**
     * **Σκέλος β του μόνιμου συνδέσμου.** Ο πόρος της διεύθυνσης ζητείται
     * ονομαστικά όταν δεν βρίσκεται στη φορτωμένη σελίδα.
     *
     * Χωρίς αυτό, σύνδεσμος προς πόρο της τρίτης σελίδας δεν άνοιγε τίποτα: ο
     * πελάτης δεν τον έχει, και η αντιπαραβολή γίνεται μόνο πάνω στα φορτωμένα.
     * Η διαδρομή είναι υπο-πόρος της συλλογής, οπότε περνά από τον **ίδιο
     * φράχτη** με τη σελίδα — σκέλος δ.
     *
     * Αποτυχία **δεν** είναι σφάλμα οθόνης: η σελίδα φορτώνει κανονικά με
     * διακριτική ειδοποίηση και η παράμετρος καθαρίζεται. Ο σύνδεσμος μπορεί να
     * είναι παλιός, ο πόρος να αποσυνδέθηκε, ή η διεύθυνση να κόπηκε στη
     * μεταφορά· καμία από αυτές δεν είναι λόγος να μη δει ο χρήστης τη συλλογή.
     */
    useEffect(() => {
        if (!state.resource) {
            setLinkedResource(null);
            return;
        }

        if (resources.some((item) => item.id === state.resource)) {
            setLinkedResource(null);
            return;
        }

        let cancelled = false;

        userService.getCollectionResource(collectionId, state.resource).then(
            (found) => { if (!cancelled) setLinkedResource(found); },
            () => {
                if (cancelled) return;
                // Το ίδιο μήνυμα για τον ανύπαρκτο πόρο και για εκείνον που δεν
                // ανήκει εδώ ή δεν επιτρέπεται: η διάκριση θα ήταν συναγωγή
                // ύπαρξης, που ο ίδιος ο διακομιστής αρνείται να κάνει.
                setLinkedResource(null);
                setLinkNotice(P1_5_TEXT.resourceLinkMissing);
                closeResource();
            }
        );

        return () => { cancelled = true; };
    }, [collectionId, state.resource, resources, closeResource]);

    // Ο τίτλος της συλλογής είναι γνωστός μόνο αφού φορτωθεί, και η γραμμή
    // πλοήγησης βρίσκεται έξω από τη σελίδα, οπότε ανακοινώνεται στο Dashboard.
    useEffect(() => () => {
        onTitle('');
        onStatus(null);
    }, [onTitle, onStatus]);

    const role = roleIn(collection);
    const canWrite = ['member', 'coordinator', 'admin'].includes(role) && !collection?.archived;
    const canManage = ['coordinator', 'admin'].includes(role);

    /**
     * Οι κάρτες που αποδίδονται.
     *
     * Το φιλτράρισμα **έφυγε από εδώ**: το κάνει ο διακομιστής πάνω σε ολόκληρη
     * τη συλλογή, όχι στη φορτωμένη σελίδα. Ό,τι μένει είναι η προσθήκη του
     * πόρου του συνδέσμου όταν ανήκει σε άλλη σελίδα — ώστε το modal να έχει τι
     * να ανοίξει χωρίς να χαθεί η σελίδα στην οποία βρίσκεται ο χρήστης.
     */
    // Ο έλεγχος «είναι ήδη στη σελίδα;» γίνεται **και εδώ**, όχι μόνο στο effect που
    // καθαρίζει το `linkedResource`. Δεν είναι διπλή βεβαιότητα: το effect εξαρτάται
    // από σειρά εκτέλεσης και η σειρά έχει κενό. Αν ο πόρος του συνδέσμου απαντήσει
    // **πριν** φτάσει η σελίδα των πόρων, υπάρχει μία απόδοση όπου το `resources` τον
    // περιέχει ήδη και το `linkedResource` δεν έχει ακόμη μηδενιστεί: η ίδια κάρτα
    // αποδίδεται δύο φορές με το ίδιο `key`. Μετρήθηκε στην κονσόλα ως «two children
    // with the same key», και το ορατό επακόλουθο ήταν ότι το React **ξανάχτιζε** τα
    // στοιχεία της λίστας, μηδενίζοντας την τοπική τους κατάσταση. Η απόδοση δεν
    // επιτρέπεται να εξαρτάται από το ποιο αίτημα απάντησε πρώτο.
    const visibleResources = linkedResource && !resources.some((item) => item.id === linkedResource.id)
        ? [linkedResource, ...resources]
        : resources;

    /**
     * Οι ετικέτες της κάρτας, **σταθερές κατά το φιλτράρισμα**.
     *
     * Υπολογίζονταν από το `resources`, δηλαδή από το **φιλτραρισμένο** αποτέλεσμα:
     * με το πρώτο πάτημα ετικέτας η λίστα συρρικνωνόταν σε όσες συνυπάρχουν με
     * αυτήν, και οι υπόλοιπες **εξαφανίζονταν από την οθόνη**. Η σύζευξη ετικετών
     * που υλοποιεί το API (`tags=a,b`) γινόταν έτσι απρόσιτη: ο χρήστης δεν είχε
     * πού να πατήσει τη δεύτερη, και η εξαφάνιση διαβαζόταν ως «το φίλτρο
     * αντικατέστησε» ενώ ο χειριστής πάντοτε **πρόσθετε**.
     *
     * Μαθαίνονται επομένως **μόνο από την αφιλτράριστη προβολή**, και οι ενεργές
     * προστίθενται πάντοτε ώστε να μένει δυνατή η αποεπιλογή τους.
     *
     * **Δηλωμένο όριο:** το σύνολο προκύπτει από τη **φορτωμένη σελίδα** και όχι
     * από ολόκληρη τη συλλογή — μια ετικέτα που εμφανίζεται μόνο στη σελίδα 4 δεν
     * φαίνεται. Η πλήρης λύση θέλει διαδρομή που επιστρέφει τις ετικέτες της
     * συλλογής, και δεν υπάρχει.
     */
    const [knownTags, setKnownTags] = useState([]);

    useEffect(() => {
        if (isFiltering(state)) return;
        setKnownTags([...new Set(resources.flatMap((resource) => resource.tags))]);
    }, [resources, state]);

    const allTags = useMemo(
        () => [...new Set([...knownTags, ...state.tags])].sort((a, b) => a.localeCompare(b, 'el')),
        [knownTags, state.tags]
    );

    /**
     * **D49 — οι ίδιες ετικέτες, ως προτάσεις στη φόρμα καταχώρησης (ΛΑ-11).**
     *
     * Ο κατάλογος είναι ο **ίδιος** που τροφοδοτεί την κάρτα «Ετικέτες» και τα
     * φίλτρα, χωρίς δεύτερο υπολογισμό και **χωρίς νέα διαδρομή**: αν οι δύο
     * προέρχονταν από ξεχωριστές πηγές, η οθόνη θα μπορούσε να προτείνει ετικέτα
     * που δεν φιλτράρει τίποτα, ή να φιλτράρει με ετικέτα που δεν προτείνει ποτέ.
     *
     * Το πεδίο κρατά **κείμενο με κόμματα**, όπως πάντοτε — η μετατροπή σε τιμές
     * γίνεται εδώ και μόνο εδώ, με τον **ίδιο** κανόνα που χρησιμοποιεί η υποβολή.
     */
    const formTags = form.tags.split(',').map((tag) => tag.trim()).filter(Boolean);

    const addFormTag = (tag) => {
        if (formTags.includes(tag)) return;
        set('tags')([...formTags, tag].join(', '));
    };

    // ═══════════════════════════════════════════════════════════════════════════
    // ΟΙ ΦΡΑΧΤΕΣ ΦΟΡΤΩΣΗΣ ΖΟΥΝ ΕΔΩ, ΑΜΕΣΩΣ ΜΕΤΑ ΤΟΝ ΤΕΛΕΥΤΑΙΟ HOOK
    // ═══════════════════════════════════════════════════════════════════════════
    //
    // **Η θέση τους είναι ο φράχτης, όχι ο κώδικάς τους.** Ήταν εξήντα γραμμές πιο
    // κάτω, μετά από μια σειρά παραγόμενων τιμών· ό,τι υπολογιζόταν ανάμεσα έτρεχε
    // με τη συλλογή **ακόμη κενή**. Όσο οι αναγνώσεις ζούσαν μέσα σε JSX, δεν
    // φαινόταν: το JSX είναι μετά τους φράχτες και δεν αποδίδεται ποτέ σε εκείνη
    // την κατάσταση. Η αναδιάρθρωση ανέβασε **μία** ανάγνωση σε παραγόμενη τιμή —
    // ίδια έκφραση, νέα θέση — και η κεντρική οθόνη έπαψε να φορτώνει.
    //
    // Πιο πάνω από εδώ **δεν γίνεται**: οι hooks πρέπει να καλούνται σε κάθε
    // απόδοση, χωρίς συνθήκη. Είναι επομένως το **νωρίτερο νόμιμο σημείο**, και
    // μετά από αυτό κάθε τιμή, κάθε χειριστής και κάθε γραμμή απόδοσης μπορεί να
    // υποθέσει ότι η συλλογή υπάρχει. Δεν είναι ευκολία: είναι ο μόνος τρόπος να
    // πάψει η κατηγορία να είναι εκφράσιμη σε αυτό το αρχείο.
    //
    // Ό,τι είναι πάνω από τους φράχτες διαβάζει με προστασία (`collection?.…`),
    // γιατί εκεί η κενή τιμή είναι **κανονική κατάσταση** και όχι σφάλμα.
    if (error && !collection) {
        return (
            <div className="flex flex-col items-start gap-4">
                <Notice status="danger" title="Η συλλογή δεν φορτώθηκε">{error}</Notice>
                <Button variant="outline" onPress={() => navigate('/')}>Επιστροφή στις συλλογές</Button>
            </div>
        );
    }

    if (!collection) {
        return (
            <div className="flex flex-col gap-6" aria-busy="true">
                <SkeletonCardColumns count={6} />
            </div>
        );
    }


    const set = (name) => (value) => setForm((current) => ({ ...current, [name]: value }));

    function openResourceForm() {
        setReplacing(null);
        setOutcome(null);
        setForm(EMPTY_RESOURCE);
        validation.reset();
        setFormError('');
        setFieldErrors({});
        setDuplicateId('');
        setMetaNotice(null);
        setMetaSources(null);
        setResourceOpen(true);
    }

    /**
     * ΛΑ-09 από το πόρταλ (Απόφαση D12).
     *
     * Τα αντλημένα **αντικαθιστούν** ό,τι υπάρχει στα τρία πεδία, γιατί το
     * κουμπί σημαίνει ρητά «συμπλήρωσε από τη σελίδα». Ό,τι η σελίδα δεν έδωσε
     * μένει ως έχει: η άντληση δεν σβήνει δουλειά του χρήστη για να βάλει κενό.
     * Η ΛΑ-10 του επιτρέπει να τα διορθώσει, και η ειδοποίηση του το ζητά ρητά.
     */
    function fetchMetadata() {
        if (localUrl) {
            validation.markSubmitted();
            setMetaNotice({
                status: 'warning',
                title: 'Λείπει η διεύθυνση',
                body: P1_5_TEXT.metadataNeedsUrl
            });
            return;
        }

        setFetchingMeta(true);
        setMetaNotice(null);

        userService.fetchUrlMetadata(form.url).then(
            (data) => {
                setForm((current) => ({
                    ...current,
                    // Η **τελική** διεύθυνση, μετά τις ανακατευθύνσεις και
                    // κανονικοποιημένη κατά τη D9: ο χρήστης βλέπει από πριν
                    // ακριβώς τι θα αποθηκευτεί (ΜΛΑ-03).
                    url: data.url || current.url,
                    title: data.title || current.title,
                    description: data.description || current.description,
                    image: data.image || current.image
                }));
                setMetaSources(data.sources);
                setFieldErrors({});
                setFetchingMeta(false);

                const redirected = data.url !== data.requestedUrl;
                setMetaNotice({
                    status: 'success',
                    title: 'Τα στοιχεία συμπληρώθηκαν',
                    body: redirected
                        ? `${P1_5_TEXT.metadataFilled} ${P1_5_TEXT.metadataRedirected}`
                        : P1_5_TEXT.metadataFilled
                });
            },
            (apiError) => {
                // Η αποτυχία **δεν** μπλοκάρει την καταχώρηση: ο χρήστης
                // συμπληρώνει με το χέρι, όπως έκανε πάντα πριν τη D12.
                setMetaNotice({
                    status: 'danger',
                    title: 'Η άντληση απέτυχε',
                    body: messageOf(apiError)
                });
                setFetchingMeta(false);
            }
        );
    }

    // Ο διακομιστής απαιτεί **έγκυρη απόλυτη διεύθυνση**. Ο παλιός έλεγχος κοίταζε
    // μόνο αν το πεδίο είναι κενό, οπότε ένα «abc» περνούσε τοπικά και απορριπτόταν
    // με 422 μετά την υποβολή.
    const localUrl = check.url(form.url);
    const localTitle = check.resourceTitle(form.title);

    const localImage = check.optionalUrl(form.image);

    const urlMessage = fieldErrors.url ?? validation.show('url', localUrl);
    const titleMessage = fieldErrors.title ?? validation.show('title', localTitle);
    const imageMessage = fieldErrors.image ?? validation.show('image', localImage);

    function createResource(event) {
        event.preventDefault();
        validation.markSubmitted();
        setFormError('');
        setFieldErrors({});
        setDuplicateId('');

        if (localUrl || localTitle || localImage) return;

        // Ίδιος έλεγχος με τη φόρμα επεξεργασίας, από την **ίδια** συνάρτηση: δύο
        // αναγνώσεις θα σήμαιναν ότι η ίδια πρόσκληση αποθηκεύεται με διαφορετικό
        // ποσό ανάλογα με το πού καταχωρήθηκε (D36).
        const payload = fundingPayloadOf(form.funding);
        if (payload.error) {
            setFormError(payload.error);
            return;
        }

        setSaving(true);

        const tags = form.tags.split(',').map((tag) => tag.trim()).filter(Boolean);

        userService.createResource(collectionId, { ...form, tags, funding: payload.funding }).then(
            ({ attached, data, discarded: lost }) => {
                // Το πρόχειρο κρατιέται **πριν** καθαριστεί η φόρμα: η προώθηση
                // χρειάζεται τις τιμές που μόλις πετάχτηκαν, και μετά το
                // `EMPTY_RESOURCE` δεν υπάρχει τρόπος να ανακτηθούν.
                const submitted = { ...form, tags, funding: payload.funding };

                setForm(EMPTY_RESOURCE);
                validation.reset();
                setSaving(false);
                setResourceOpen(false);

                // ΛΑ-08 και D9: δύο διαφορετικές επιτυχείς εκβάσεις, δύο μηνύματα.
                // Το `attached` σημαίνει ότι η διεύθυνση υπήρχε ήδη και εντάχθηκε η
                // υπάρχουσα εγγραφή — δεν δημιουργήθηκε τίποτα, και τα στοιχεία που
                // πληκτρολόγησε ο χρήστης αγνοήθηκαν.
                setOutcome(attached ? RESOURCE_CREATED.attached : RESOURCE_CREATED.created);

                // Ο κατάλογος συνοδεύει **μόνο** την προσάρτηση: στη δημιουργία
                // δεν απορρίφθηκε τίποτα, και ένα κενό πλαίσιο εκεί θα ήταν
                // ερώτημα χωρίς αντικείμενο.
                setDiscarded(
                    attached && lost.length > 0
                        ? { entries: lost, resourceId: data?.id ?? null, values: submitted }
                        : { entries: [], resourceId: null, values: null }
                );

                // Απόφαση D10, σκέλος β: η αποσύνδεση του παλιού γίνεται ΜΟΝΟ εδώ,
                // αφού ο νέος πόρος υπάρχει. Αποτυχία της δεν αναιρεί την
                // καταχώρηση: ο χρήστης βλέπει και τους δύο και αφαιρεί χειροκίνητα.
                if (replacing) {
                    const previous = replacing;
                    setReplacing(null);
                    userService.deleteResource(collectionId, previous.id).then(
                        () => fetchAll(),
                        (apiError) => {
                            setError(messageOf(apiError));
                            fetchAll();
                        }
                    );
                    return;
                }

                fetchAll();
            },
            (apiError) => {
                // ΛΑ-13: το API επιστρέφει το αναγνωριστικό του υπάρχοντος πόρου,
                // ώστε ο χρήστης να οδηγηθεί σε αυτόν αντί να δημιουργήσει διπλότυπο.
                setFormError(messageOf(apiError));
                setFieldErrors(fieldErrorsOf(apiError));
                setDuplicateId(apiError.status === 409 ? (apiError.details?.resourceId ?? '') : '');
                setSaving(false);
            }
        );
    }

    /**
     * Απόφαση D10, σκέλος β. Ανοίγει τη φόρμα καταχώρησης με τα μεταδεδομένα του
     * παλιού πόρου, ώστε η διόρθωση λάθος διεύθυνσης να μη σημαίνει ότι ο χρήστης
     * ξαναγράφει τίτλο, περιγραφή και ετικέτες. Η ΔΙΕΥΘΥΝΣΗ μένει κενή: αυτή
     * ακριβώς διορθώνεται.
     *
     * Τίποτα δεν αφαιρείται εδώ. Ο παλιός πόρος αποσυνδέεται μόνο μετά από
     * επιτυχή δημιουργία του νέου, στη `createResource`.
     */
    function startAddressReplacement(resource) {
        setReplacing(resource);
        setOutcome(null);
        // Κάθε πεδίο επιμέλειας, από έναν κατάλογο και όχι με απαρίθμηση στο χέρι:
        // η απαρίθμηση είναι που έχασε την εικόνα.
        setForm(replacementDraftOf(resource));
        validation.reset();
        setFormError('');
        setFieldErrors({});
        setDuplicateId('');
        // Οι ενδείξεις προέλευσης αφορούν την προηγούμενη άντληση και δεν
        // ισχύουν για τα μεταφερμένα πεδία: αυτά τα έγραψε άνθρωπος.
        setMetaNotice(null);
        setMetaSources(null);
        setResourceOpen(true);
    }

    function addMember(event) {
        event.preventDefault();
        if (!newMember) return;

        userService.addCollectionMember(collectionId, newMember.toLowerCase()).then(
            (updated) => {
                setCollection(updated);
                setNewMember('');
                setMemberError('');
            },
            (apiError) => setMemberError(messageOf(apiError))
        );
    }

    function removeMember(userId) {
        userService.removeCollectionMember(collectionId, userId).then(
            () => fetchAll(),
            (apiError) => setMemberError(messageOf(apiError))
        );
    }

    // Ο ίδιος έλεγχος με τη δημιουργία (**2** χαρακτήρες, όσους απαιτεί ο
    // διακομιστής), από την ίδια συνάρτηση: δύο αντίγραφα του κανόνα θα άφηναν την
    // επεξεργασία να δέχεται τίτλο που η δημιουργία απορρίπτει.
    const editTitleError = editForm ? check.collectionTitle(editForm.title) : undefined;
    const editTitleMessage = editFieldErrors.title ?? editValidation.show('title', editTitleError);

    /**
     * ΛΑ-05: η επεξεργασία της συλλογής, με τα τέσσερα στοιχεία που ονομάζει η
     * απαίτηση — τίτλος, θεματικό αντικείμενο, περιγραφή και επίπεδο πρόσβασης.
     *
     * Η φόρμα ανοίγει **από τα φορτωμένα δεδομένα** και όχι από κενό: η ΛΑ-05
     * ζητά επεξεργασία, δηλαδή διόρθωση του υπάρχοντος. Φόρμα που ανοίγει άδεια
     * μετατρέπει κάθε διόρθωση σε επαναπληκτρολόγηση.
     */
    function openEditForm() {
        setEditForm({
            title: collection.title ?? '',
            topic: collection.topic ?? '',
            description: collection.description ?? '',
            visibility: collection.visibility
        });
        editValidation.reset();
        setEditError('');
        setEditFieldErrors({});
        setEditOpen(true);
    }

    /**
     * **Μία διαδρομή, και δεν παρακάμπτεται τίποτα.**
     *
     * Η μεταβολή ορατότητας έχει συνέπειες που είναι ήδη υλοποιημένες και
     * δοκιμασμένες αλλού: οι εξωτερικές παραδόσεις σταματούν, το κλειδί
     * πρόσβασης της συλλογής παύει να διαβάζει, η συλλογή φεύγει από την
     * ανακάλυψη, και η ροή της γίνεται αθέατη σε μη μέλη. **Καμία από αυτές δεν
     * είναι παρενέργεια αυτής της φόρμας**: όλες προκύπτουν από την τιμή του
     * πεδίου τη στιγμή που κάποιος ρωτά, όχι από γεγονός που εκπέμπεται εδώ. Η
     * φόρμα γράφει το πεδίο μέσω της **μίας** διαδρομής που το γράφει, και
     * τελειώνει εκεί.
     */
    function saveCollection(event) {
        event.preventDefault();
        editValidation.markSubmitted();
        setEditError('');
        setEditFieldErrors({});

        if (editTitleError) return;

        setSavingEdit(true);
        userService.updateCollection(collectionId, editForm).then(
            (updated) => {
                setCollection(updated);
                // Ο τίτλος και τα chips ζουν στην πάνω μπάρα, που δεν παρακολουθεί
                // τη συλλογή: χωρίς αυτό θα έμεναν στάσιμα, όπως ακριβώς θα
                // έμενε το chip «Αρχειοθετημένο» χωρίς το ίδιο ζεύγος παρακάτω.
                onTitle(updated.title);
                onStatus({
                    visibility: updated.visibility,
                    archived: updated.archived,
                    topic: updated.topic
                });
                setSavingEdit(false);
                setEditOpen(false);
            },
            (apiError) => {
                setEditError(messageOf(apiError));
                setEditFieldErrors(fieldErrorsOf(apiError));
                setSavingEdit(false);
            }
        );
    }

    /**
     * ΛΑ-04: η μεταβολή ρόλου, δηλαδή ο τρόπος με τον οποίο ο συντονιστής ορίζει
     * **άλλον** συντονιστή.
     *
     * Ο φράχτης «η συλλογή δεν μένει χωρίς συντονιστή» ζει στον εξυπηρετητή και
     * δεν επαναλαμβάνεται εδώ. Η άρνησή του φτάνει ως μήνυμα δίπλα στη λίστα.
     */
    function changeMemberRole(userId, role) {
        setMemberError('');
        userService.updateCollectionMemberRole(collectionId, userId, role).then(
            (updated) => setCollection(updated),
            (apiError) => setMemberError(messageOf(apiError))
        );
    }

    function toggleArchive() {
        const action = collection.archived
            ? userService.unarchiveCollection(collectionId)
            : userService.archiveCollection(collectionId);

        action.then(
            (updated) => {
                setCollection(updated);
                // Χωρίς αυτό το chip «Αρχειοθετημένο» της μπάρας θα έμενε στάσιμο:
                // η μπάρα ζει στο κέλυφος και δεν παρακολουθεί τη συλλογή.
                onStatus({
                    visibility: updated.visibility,
                    archived: updated.archived,
                    topic: updated.topic
                });
            },
            (apiError) => setError(messageOf(apiError))
        );
    }

    /**
     * ΜΛΑ-03. Ανοίγει την επιβεβαίωση **και ρωτά τι θα χαθεί**.
     *
     * Η μέτρηση γίνεται εδώ και όχι στη φόρτωση της σελίδας: πλήθη υπολογισμένα
     * τότε είναι όσο παλιά είναι η φόρτωση, και ένας συνεργάτης που πρόσθεσε δέκα
     * πόρους στο μεταξύ δεν φαίνεται. Ψευδής αριθμός σε μη αναστρέψιμη ενέργεια
     * είναι χειρότερος από κανέναν, γιατί ο χρήστης τον εμπιστεύεται.
     */
    function askToDelete() {
        setRemoval(null);
        setRemovalFailed(false);
        setDeleteOpen(true);

        userService.getCollectionRemovalPreview(collectionId).then(
            setRemoval,
            () => setRemovalFailed(true)
        );
    }

    function deleteCollection() {
        setDeleting(true);
        userService.deleteCollection(collectionId).then(
            () => navigate('/'),
            (apiError) => {
                setError(messageOf(apiError));
                setDeleting(false);
                setDeleteOpen(false);
            }
        );
    }

    // ── Παράγωγα της νέας διάταξης (Σετ Β) ──────────────────────────────────

    /**
     * **Ποιος αποδίδει τον διάλογο του πόρου.** Μία απάντηση, δύο καταναλωτές.
     *
     * Η συνάρτηση ζει στο `browseState.js` και δοκιμάζεται εκεί, με ισχυρισμό
     * που τρέχει πάνω σε **ολόκληρο** τον κατάλογο καρτελών: για κάθε καρτέλα με
     * ανοιχτό πόρο υπάρχει **ακριβώς ένας** οικοδεσπότης. Δύο συνθήκες απόδοσης
     * γραμμένες χωριστά εδώ θα μπορούσαν να αποκλίνουν, και οι δύο τρόποι
     * αποτυχίας —κανένας διάλογος, δύο διάλογοι— είναι σιωπηλοί.
     */
    const { host: dialogHost, resource: dialogResource } = resourceDialogPlan(state, {
        resources,
        linked: linkedResource
    });

    /** Πόσα δομημένα φίλτρα είναι ενεργά, για το πλήθος στο κουμπί (D30). */
    const activeFilterCount =
        (state.tags?.length ?? 0) +
        ['createdBy', 'from', 'to', 'agency', 'field', 'deadlineBefore', 'minAmount', 'eligible', 'relevance']
            .filter((name) => Boolean(state[name])).length;

    const archiveIcon = collection.archived ? 'fa-solid fa-box-open' : 'fa-solid fa-box-archive';

    // Οι χειριστές δίνονται ως **ονόματα** και όχι ως ενσωματωμένα βέλη: ο
    // στατικός φράχτης καλωδίωσης διαβάζει τα χαρακτηριστικά ως κείμενο μέχρι το
    // πρώτο `>`, και το βέλος περιέχει ένα.
    const handleSort = (key) => setSort(String(key));
    const dismissMemberError = () => setMemberError('');

    /** Η σύζευξη ετικετών: το πάτημα **προσθέτει**, δεν αντικαθιστά (ΛΑ-15). */
    function toggleTag(tag) {
        setFilter({
            tags: state.tags.includes(tag)
                ? state.tags.filter((other) => other !== tag)
                : [...state.tags, tag]
        });
    }

    /**
     * Άνοιγμα πόρου **με πρόθεση**, από τις λίστες των κρίσεων.
     *
     * Η ταυτότητα του πόρου πάει στη **διεύθυνση**, όπως πάντα· η πρόθεση μένει
     * σε κατάσταση της σελίδας. Η διάκριση δεν είναι στιλ: η ταυτότητα πρέπει να
     * επιβιώνει σε κοινοποιημένο σύνδεσμο και σε ανανέωση, η πρόθεση όχι — ο
     * παραλήπτης ενός συνδέσμου δεν πάτησε καμία κάρτα.
     */
    function openResourceFrom(resourceId, pane = 'resource') {
        setLandingPane(pane);
        openResource(resourceId);
    }

    /**
     * **P1-5β: η προώθηση γεμίζει φόρμα και ΔΕΝ αποθηκεύει.**
     *
     * Ανοίγει τον υπάρχοντα πόρο με τις τιμές που ο χρήστης μόλις έστειλε
     * συμπληρωμένες στη φόρμα επεξεργασίας του. Η αποθήκευση είναι **δεύτερο,
     * ρητό βήμα**, με τα δικαιώματα και το ίχνος του ανθρώπου που την πατά —
     * αλλιώς η D9 θα παραβιαζόταν από την πίσω πόρτα: ο ένας πελάτης θα
     * ξανάγραφε την επιμέλεια του άλλου, απλώς με ένα κλικ παραπάνω.
     */
    /** Το πρόχειρο καταναλώνεται μία φορά: ο διάλογος το ζητά και το σβήνει. */
    function consumeForwarded() {
        setForwarded(null);
    }

    function forwardDiscarded() {
        const { resourceId, values } = discarded;
        if (!resourceId || !values) return;

        setOutcome(null);
        setDiscarded({ entries: [], resourceId: null, values: null });
        setForwarded({ resourceId, values });
        openResource(resourceId);
    }

    /** Ο χειριστής ανοίγματος κάθε κάρτας, με τον πόρο στο περιβάλλον του (D15). */
    function makeOpenChange(resourceId) {
        return function handleOpenChange(open) {
            return open ? openResource(resourceId) : closeResource();
        };
    }

    /** Το ίδιο, για τον διάλογο που φιλοξενεί η σελίδα εκτός καρτέλας Πόρων. */
    function handleLinkedOpenChange(open) {
        if (!open) closeResource();
    }

    return (
        <div className="flex flex-col gap-6">
            <Breadcrumbs>
                <Breadcrumbs.Item href="/">Συλλογές</Breadcrumbs.Item>
                <Breadcrumbs.Item>{collection.title}</Breadcrumbs.Item>
            </Breadcrumbs>

            {error && <Notice status="danger" title="Η ενέργεια απέτυχε" onDismiss={() => setError('')}>{error}</Notice>}

            {/* ΛΑ-08 και D9: η καταχώρηση έχει δύο επιτυχείς εκβάσεις που δεν είναι
                η ίδια. Η προσάρτηση δεν εμφανίζεται ως επιτυχία δημιουργίας, γιατί
                δεν δημιουργήθηκε τίποτα.

                **Ήταν «πληροφορία» και έγινε «προσοχή» (19/08).** Δεν είναι
                υποβάθμιση της επιτυχίας: η ενέργεια πέτυχε. Είναι ότι ο χρήστης
                **έχασε δουλειά χωρίς να το ζητήσει** — ο τίτλος και η περιγραφή που
                πληκτρολόγησε απορρίφθηκαν για να μην αντικατασταθεί η επιμέλεια
                άλλων — και μια τέτοια έκβαση δεν ανακοινώνεται με τον ίδιο τόνο που
                ανακοινώνεται μια συνηθισμένη καταχώρηση. */}
            {outcome && (
                /* **Η προσάρτηση χρειάζεται δική της έμφαση, και δεν είναι θέμα
                   γούστου.** Είναι το μόνο μήνυμα της οθόνης που εξηγεί δύο πράγματα
                   τα οποία ο χρήστης δεν μπορεί να συμπεράνει από πουθενά αλλού: ότι
                   ο πόρος ήρθε με **ξένη επιμέλεια**, και ότι ό,τι πληκτρολόγησε ο
                   ίδιος **δεν αποθηκεύτηκε**. Με την ίδια διακριτική εμφάνιση που
                   έχει το «καταχωρήθηκε», διαβαζόταν ως επιβεβαίωση ρουτίνας και
                   κλεινόταν χωρίς ανάγνωση — και μετά η απορία «γιατί ο τίτλος μου
                   δεν είναι εκεί» δεν είχε απάντηση πουθενά στην οθόνη.

                   Δεν κλείνει μόνο του και δεν συνοδεύεται από `onDismiss` όταν είναι
                   προσάρτηση: το μήνυμα είναι ο λόγος που η οθόνη δείχνει κάτι
                   διαφορετικό από ό,τι έγραψε ο χρήστης. */
                <Notice
                    status={outcome === RESOURCE_CREATED.attached ? 'warning' : 'success'}
                    title={outcome.title}
                    onDismiss={() => setOutcome(null)}
                    className={outcome === RESOURCE_CREATED.attached
                        ? 'border-2 shadow-sm'
                        : ''}
                >
                    {/* Έντονα και σε κανονικό χρώμα κειμένου, όχι στο δεύτερης τάξης
                        γκρι της περιγραφής: είναι το μόνο μήνυμα που εξηγεί γιατί η
                        οθόνη δείχνει κάτι διαφορετικό από ό,τι πληκτρολόγησε ο χρήστης. */}
                    {outcome === RESOURCE_CREATED.attached
                        ? <strong className="font-semibold text-foreground">{outcome.body}</strong>
                        : outcome.body}

                    {/* P1-5β: **τι ακριβώς** πετάχτηκε, μέσα στο ίδιο μήνυμα που
                        λέει ότι η καταχώρηση έγινε προσάρτηση. Χωριστό μήνυμα θα
                        χώριζε την αιτία από το αποτέλεσμα. */}
                    <DiscardedCuration discarded={discarded.entries} onForward={forwardDiscarded} />
                </Notice>
            )}

            {/* Σκέλος β του μόνιμου συνδέσμου: **διακριτική ειδοποίηση, ποτέ κενή
                οθόνη ή σφάλμα**. Ο σύνδεσμος μπορεί να είναι παλιός ή κομμένος
                στη μεταφορά· τίποτε από αυτά δεν είναι λόγος να μη δει ο χρήστης
                τη συλλογή. Το ίδιο μήνυμα και όταν ο πόρος δεν επιτρέπεται. */}
            {linkNotice && (
                <Notice status="info" title="Ο σύνδεσμος δεν άνοιξε" onDismiss={() => setLinkNotice('')}>
                    {linkNotice}
                </Notice>
            )}

            {/* Ο τίτλος και τα chips ζουν στην πάνω μπάρα. Εδώ μένει μόνο η
                περιγραφή: ήταν τρία στοιχεία δύο φορές στην ίδια οθόνη.

                ⚠ **ΣΕ ΔΙΚΟ ΤΗΣ ΠΛΑΙΣΙΟ, ΜΕ ΤΙΤΛΟ.** Ήταν ελεύθερη παράγραφος
                ανάμεσα στη μπάρα και στη γραμμή ενεργειών: κείμενο χωρίς όριο,
                που το μάτι δεν ήξερε αν είναι περιγραφή, οδηγία ή μήνυμα. Το
                ίδιο σχήμα με την περιγραφή **του πόρου** — και δεν είναι
                σύμπτωση: το ίδιο είδος περιεχομένου παίρνει το ίδιο σχήμα, ώστε
                ο χρήστης να το αναγνωρίζει χωρίς να διαβάσει τον τίτλο.

                Ο τίτλος υπάρχει επειδή το πλαίσιο **δεν αρκεί**: ένα κουτί με
                κείμενο δίπλα στο όνομα μιας συλλογής μπορεί εξίσου να είναι
                ανακοίνωση ή προειδοποίηση. Είναι `<h2>` και όχι έντονο κείμενο,
                γιατί είναι πραγματική επικεφαλίδα ενότητας — ο αναγνώστης οθόνης
                πηδά σε αυτήν. */}
            {collection.description && (
                <section className="rounded-xl border border-border bg-surface p-4">
                    <h2 className="mb-1 text-sm font-semibold">{P1_5_TEXT.collectionDescription}</h2>
                    <p className="whitespace-pre-line text-muted">{collection.description}</p>
                </section>
            )}

            {/* ══ ΓΡΑΜΜΗ ΕΝΕΡΓΕΙΩΝ ΤΗΣ ΣΥΛΛΟΓΗΣ — ΕΞΩ από τις καρτέλες ══════════
                Οι ενέργειες αφορούν τη **συλλογή ως όλον** και όχι το περιεχόμενο
                κάποιας καρτέλας. Μέσα σε καρτέλα, η διαγραφή —μη αναστρέψιμη— θα
                ήταν κρυμμένη πίσω από αυθαίρετη επιλογή προβολής.

                Ήταν δύο κάρτες στην πλαϊνή στήλη, «Διαχείριση» και «Διάθεση
                δεδομένων», και ήταν **σκόπιμα χωριστές**: η πρώτη αποδίδεται σε
                όποιον γράφει, η δεύτερη σε όποιον **βλέπει** — ο φράχτης της
                εξαγωγής είναι το `collection:viewPublic`, ο ίδιος που επιτρέπει
                την ανάγνωση της σελίδας. Η διάκριση **επιβιώνει** εδώ, ως μία
                γραμμή με διαφορετική συνθήκη ανά κουμπί, αντί να χρειάζεται να
                γραφτεί ξανά μέσα σε κάθε καρτέλα που θα τις φιλοξενούσε.

                Το «Προσθήκη πόρου» **δεν** είναι εδώ: είναι ενέργεια πόρου και ζει
                στη γραμμή φίλτρων της καρτέλας των Πόρων. Υπήρχε ως τώρα σε δύο
                σημεία —ένα για πλατιά και ένα για στενή οθόνη— και μένει **ένα**,
                ορατό σε κάθε πλάτος. */}
            <div className="flex flex-wrap items-center gap-2">
                <ExportJsonLd
                    path={`/api/collections/${collectionId}/export.jsonld`}
                    fallbackName="collection.jsonld"
                    size="sm"
                />
                {canManage && (
                    <Button variant="outline" size="sm" onPress={openEditForm}>
                        <i className="fa-solid fa-pen" aria-hidden="true" />
                        Επεξεργασία
                    </Button>
                )}
                {canManage && (
                    <Button variant="outline" size="sm" onPress={toggleArchive}>
                        <i className={archiveIcon} aria-hidden="true" />
                        {collection.archived ? 'Επαναφορά' : 'Αρχειοθέτηση'}
                    </Button>
                )}
                {canManage && (
                    <Button variant="danger" size="sm" onPress={askToDelete} className="ms-auto">
                        <i className="fa-solid fa-trash" aria-hidden="true" />
                        Διαγραφή
                    </Button>
                )}
            </div>

            {/* ══ ΟΙ ΚΑΡΤΕΛΕΣ (Απόφαση D27) ═════════════════════════════════════
                Η σελίδα κουβαλούσε πόρους, ετικέτες, μέλη, κρίσεις, καθήκοντα,
                δραστηριότητα, φίλτρα και διαχείριση σε **μία** στήλη με πλαϊνή.

                Το `ViewTab` είναι το **υπάρχον** συστατικό, τρίτος καταναλωτής μετά
                τον κατάλογο συλλογών και το παράθυρο πόρου. Το `aria-pressed` που
                φέρει δεν είναι διακοσμητικό: χωρίς αυτό ο αναγνώστης οθόνης ακούει
                τέσσερα κουμπιά και καμία ένδειξη ποιο είναι ενεργό — η πληροφορία
                θα υπήρχε μόνο στο χρώμα (ΜΛΑ-11, WCAG 1.4.1).

                Ο κατάλογος **διαβάζεται**, δεν απαριθμείται. */}
            <div
                role="group"
                aria-label={P1_5_TEXT.tabsLabel}
                className="flex flex-wrap items-center gap-1 self-start rounded-full border border-border bg-surface p-1"
            >
                {TABS.collection.map((tab) => (
                    <CollectionTab key={tab} tab={tab} active={state.tab} onSelect={setTab} />
                ))}
            </div>

            {/* ── ΚΑΡΤΕΛΑ: ΠΟΡΟΙ ──────────────────────────────────────────────── */}
            {state.tab === 'resources' && (
                <div className="flex min-w-0 flex-col gap-4">
                        {/* **Απόφαση D30: μία γραμμή.** Αναζήτηση, κουμπί που
                            επεκτείνει την περιοχή των φίλτρων, και μικρότερη
                            σκανδάλη ταξινόμησης που ανοίγει τις επιλογές. Ως τώρα
                            όλα ήταν ανοιχτά και έπιαναν το μισό της οθόνης.

                            **Αλλάζει η εμφάνιση, όχι η συμπεριφορά**: τα φίλτρα
                            εξακολουθούν να ζουν στη διεύθυνση και να εφαρμόζονται
                            στον διακομιστή, ακριβώς όπως πριν. */}
                        <div className="flex flex-wrap items-end gap-2">
                            <SearchField
                                value={term}
                                onChange={setTerm}
                                className="min-w-48 flex-1"
                                aria-label="Αναζήτηση πόρων"
                            >
                                <SearchField.Group>
                                    <SearchField.SearchIcon />
                                    <SearchField.Input placeholder="Τίτλος, περιγραφή, διεύθυνση ή ετικέτα" />
                                    <SearchField.ClearButton />
                                </SearchField.Group>
                            </SearchField>

                            {/* Το πλήθος στο κουμπί, ώστε η σύμπτυξη να μην κρύβει
                                **ότι** φιλτράρει κάτι. Το **ποια** το λένε τα chips
                                παρακάτω: ο αριθμός μόνος του δεν αφαιρείται με ένα
                                πάτημα. */}
                            <Button
                                variant={areFiltersOpen ? 'secondary' : 'outline'}
                                onPress={toggleFilters}
                                aria-expanded={areFiltersOpen}
                            >
                                <i className="fa-solid fa-sliders" aria-hidden="true" />
                                {P1_5_TEXT.filtersToggle}
                                {activeFilterCount > 0 && (
                                    <span className="ms-1 rounded-full bg-accent px-2 text-xs text-accent-foreground">
                                        {activeFilterCount}
                                    </span>
                                )}
                            </Button>

                            {/* ΛΑ-15: η ταξινόμηση γίνεται στον διακομιστή, ώστε να
                                αφορά ολόκληρη τη συλλογή. Ταξινόμηση στον πελάτη θα
                                αναδιέτασσε **τη σελίδα**, δηλαδή θα έδειχνε ως
                                «νεότερο» το νεότερο των είκοσι.

                                Η ετικέτα έγινε `aria-label` αντί για ορατό `<Label>`:
                                το στοιχείο είναι πλέον σκανδάλη σε γραμμή, όχι πεδίο
                                φόρμας. Ο αναγνώστης οθόνης ακούει το ίδιο. */}
                            <Select placeholder={SELECT_PLACEHOLDER}
                                selectedKey={`${state.sort}:${state.order}`}
                                onSelectionChange={handleSort}
                                aria-label={P1_5_TEXT.sortLabel}
                                className="w-auto"
                            >
                                {/* ⚠ **ΤΟ ΕΙΚΟΝΙΔΙΟ ΚΑΙ ΤΟ ΚΕΙΜΕΝΟ ΗΤΑΝ ΚΟΛΛΗΜΕΝΑ ΚΑΙ
                                    ΑΣΤΟΙΧΙΣΤΑ.** Η σκανδάλη δεν όριζε ούτε απόσταση
                                    ούτε κάθετη στοίχιση, οπότε το εικονίδιο ακουμπούσε
                                    το κείμενο και καθόταν λίγο ψηλότερα από αυτό — δύο
                                    στοιχεία που διαβάζονται ως ένα, χωρίς να είναι.

                                    Το `items-center` δεν είναι πλεονασμός: το εικονίδιο
                                    και το κείμενο έχουν **διαφορετικό ύψος γραμμής**,
                                    οπότε χωρίς αυτό στοιχίζονται στη βάση τους και όχι
                                    στο κέντρο τους. */}
                                <Select.Trigger className="flex items-center gap-2">
                                    <i className="fa-solid fa-arrow-down-short-wide" aria-hidden="true" />
                                    <Select.Value />
                                    <Select.Indicator />
                                </Select.Trigger>
                                <Select.Popover>
                                    <ListBox items={SORT_OPTIONS.resources}>
                                        {(item) => <ListBoxItem id={item.id}>{item.label}</ListBoxItem>}
                                    </ListBox>
                                </Select.Popover>
                            </Select>

                            {/* ⚠ **ΤΟ «ΠΡΟΣΘΗΚΗ ΠΟΡΟΥ» ΕΦΥΓΕ ΑΠΟ ΕΔΩ** και έγινε
                                **αιωρούμενο** κουμπί, στο κάτω δεξιά άκρο της οθόνης
                                — βλ. το τέλος αυτού του συστατικού.

                                Η γραμμή κρατά **φίλτρα και ταξινόμηση**, που είναι
                                ρυθμίσεις της προβολής· η καταχώρηση είναι **ενέργεια**,
                                και ήταν η μόνη ενέργεια μέσα σε μια γραμμή ρυθμίσεων.
                                Σε στενή οθόνη μάλιστα έπεφτε σε δεύτερη γραμμή, δηλαδή
                                η κύρια ενέργεια της σελίδας κρυβόταν κάτω από τα
                                φίλτρα της. */}
                        </div>

                        {/* Η περιοχή που επεκτείνεται. Μέσα της τα δομημένα φίλτρα
                            **και οι ετικέτες**: η κάρτα των ετικετών ήταν φίλτρο με
                            ένα πάτημα, και φίλτρο ανήκει δίπλα σε αυτό που φιλτράρει.
                            Πατημένη από πλαϊνή στήλη, το αποτέλεσμά της άλλαζε αλλού.

                            **Δηλωμένο τίμημα:** η κάρτα υπηρετούσε **και** επισκόπηση
                            του λεξιλογίου της συλλογής (ΛΑ-11), και με τα φίλτρα
                            κλειστά η επισκόπηση κρύβεται. Αντισταθμίζεται από τα chips
                            των ενεργών, που μένουν πάντοτε ορατά. */}
                        {areFiltersOpen && (
                            <div className="flex flex-col gap-4 rounded-2xl border border-border p-4">
                                <FundingFilters state={state} setFilter={setFilter} showRelevance />

                                <fieldset className="flex flex-col gap-2">
                                    <legend className="text-sm font-semibold">Ετικέτες</legend>
                                    {allTags.length > 0 ? (
                                        <div className="flex flex-wrap gap-2">
                                            {allTags.map((tag) => (
                                                <TagFilterButton
                                                    key={tag}
                                                    tag={tag}
                                                    isActive={state.tags.includes(tag)}
                                                    onToggle={toggleTag}
                                                />
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-sm text-muted">Καμία ετικέτα σε αυτή τη συλλογή ακόμη.</p>
                                    )}
                                </fieldset>
                            </div>
                        )}

                        {/* ΛΑ-15: τα ενεργά φίλτρα, με τρόπο να αφαιρεθούν, **έξω**
                            από τη συμπτυσσόμενη περιοχή. Φίλτρο που δεν φαίνεται
                            είναι φίλτρο που ο χρήστης θα ξεχάσει, και θα διαβάσει το
                            μικρό αποτέλεσμα ως έλλειψη δεδομένων. Ο κανόνας ίσχυε
                            ήδη για τις ετικέτες· η σύμπτυξη τον χρειάζεται για όλα. */}
                        <ActiveFilters state={state} setFilter={setFilter} />

                        {visibleResources.length === 0 ? (
                            <EmptyResult
                                isSearching={isFiltering(state)}
                                emptyMessage="Η συλλογή δεν έχει πόρους ακόμη."
                            />
                        ) : (
                            // Δύο στήλες και όχι τρεις: η κάρτα πόρου έχει εικόνα,
                            // τίτλο δύο γραμμών, περιγραφή τριών και υποσέλιδο. Στα
                            // ~300 πίξελ η περιγραφή γίνεται στήλη μιας λέξης.
                            // **Τρεις στήλες σε πλατιά οθόνη**, από τη στιγμή που η
                            // πλαϊνή στήλη έφυγε. Ήταν δύο όχι επειδή δεν χωρούσαν
                            // τρεις, αλλά επειδή 20rem πήγαιναν σε σύνοψη που έχει
                            // πλέον δική της καρτέλα.
                            // ⚠ **ΤΕΤΑΡΤΗ ΣΤΗΛΗ ΠΑΝΩ ΑΠΟ ΤΑ 1900 ΠΙΞΕΛ.** Το κατώφλι είναι
                            // **ρητό και όχι το `2xl` του πλαισίου** (1536), γιατί εκεί
                            // η τέταρτη στήλη θα έδινε κάρτες στενότερες από την
                            // αναλογία της εικόνας τους: η κάρτα έχει σταθερή αναλογία
                            // 1.91:1, οπότε κάθε στήλη που στενεύει κονταίνει και το
                            // μισό της κάρτας. Στα 1900 και πάνω το πλάτος φτάνει.

                            // Οι στήλες παραμένουν `columns` και όχι πλέγμα: οι κάρτες
                            // έχουν **ανόμοιο ύψος** —περιγραφή, πλήθη, χρηματοδότηση—
                            // και το πλέγμα θα τις τέντωνε όλες στο ύψος της
                            // ψηλότερης της γραμμής.
                            <div className="columns-1 gap-4 md:columns-2 xl:columns-3 columns-wide-4">
                                {visibleResources.map((resource) => (
                                    <ResourceItem
                                        key={resource.id}
                                        resource={resource}
                                        collectionId={collectionId}
                                        canEdit={canWrite}
                                        onChanged={fetchAll}
                                        onReplace={startAddressReplacement}
                                        // Ο πόρος του συνδέσμου καρφιτσώνεται στην κορυφή
                                        // όταν ανήκει σε άλλη σελίδα, και **το δηλώνει**.
                                        // Χωρίς τη σήμανση η κάρτα εμφανίζεται σε θέση που
                                        // δεν αντιστοιχεί στην ταξινόμηση και μοιάζει με
                                        // σφάλμα· με αυτήν, η εξαίρεση είναι εξηγημένη.
                                        isPinnedByLink={linkedResource?.id === resource.id}
                                        // Σκέλη α και γ: το άνοιγμα γράφει τη διεύθυνση,
                                        // το κλείσιμο την αναιρεί, και το κουμπί
                                        // επιστροφής κλείνει το modal αντί να φεύγει από
                                        // τη σελίδα (D15).
                                        //
                                        // **Ο διάλογος ανοίγει από ΕΔΩ μόνο όταν ο
                                        // οικοδεσπότης είναι η λίστα** (Β1δ): η απόφαση
                                        // έρχεται από μία συνάρτηση, ώστε αυτό το σημείο
                                        // και εκείνο της σελίδας να μην μπορούν να
                                        // αποδώσουν και τα δύο ή κανένα.
                                        siblings={resources}
                                        knownTags={allTags}
                                        forwardedDraft={forwarded?.resourceId === resource.id ? forwarded.values : null}
                                        onForwardConsumed={consumeForwarded}
                                        isOpen={dialogHost === 'list' && dialogResource?.id === resource.id}
                                        onOpenChange={makeOpenChange(resource.id)}
                                    />
                                ))}
                            </div>
                        )}

                    <Pagination meta={meta} onPage={setPage} noun="πόροι" />
                </div>
            )}

            {/* ── ΚΑΡΤΕΛΑ: ΣΧΟΛΙΑ ΚΑΙ ΑΞΙΟΛΟΓΗΣΕΙΣ ────────────────────────────── */}
            {/* ══ ΣΗΜΕΙΟ ΠΡΟΣΑΡΤΗΣΗΣ P1-3 — σχόλια και αξιολογήσεις ══════════════
                Συμπληρώθηκε από το P1-3 με μία γραμμή, όπως προβλεπόταν.

                **Απέκτησε δική της καρτέλα**, και η αντίρρηση που την κρατούσε
                πλαϊνή κάρτα **έπαψε να ισχύει**. Εκείνη έλεγε: η μόνη ενέργεια της
                κάρτας ανοίγει πόρο, άρα θα άλλαζε καρτέλα. Δεν αλλάζει — ο διάλογος
                του πόρου είναι **ορθογώνιος** στις καρτέλες (D27), και ο
                οικοδεσπότης της σελίδας τον αποδίδει **πάνω** στην καρτέλα των
                κρίσεων, χωρίς πλοήγηση.

                Το τίμημα της παλιάς θέσης ήταν ορατό στην οθόνη: η πλαϊνή στήλη
                κρατούσε 20rem και το πλέγμα των πόρων έμενε σε **δύο** στήλες αντί
                για τρεις. Η σύνοψη δύο σύντομων λιστών κόστιζε το ένα τρίτο του
                πλάτους της κύριας προβολής.

                Διαθέσιμα εδώ: collection, collectionId, resources, canWrite,
                canManage, fetchAll, openResource. */}
            {state.tab === 'judgements' && (
                <CollectionJudgements resources={resources} onOpenResource={openResourceFrom} />
            )}
            {/* ══ ΤΕΛΟΣ P1-3 ═══════════════════════════════════════════════════ */}

            {/* ── ΚΑΡΤΕΛΑ: ΚΑΘΗΚΟΝΤΑ ──────────────────────────────────────────── */}
            {/* ══ ΣΗΜΕΙΟ ΠΡΟΣΑΡΤΗΣΗΣ P1-4 — καθήκοντα της συλλογής ══════════════
                Συμπληρώθηκε από το P1-4 με μία γραμμή, όπως προβλεπόταν.

                **Μετακόμισε σε δική του καρτέλα** με το Σετ Β, και δεν καταργήθηκε:
                το §4.3.4 ονομάζει ρητά τη λογική Kanban, και τρεις στήλες δεν χωρούν
                σε πλαϊνή στήλη 20rem. Τα props δεν άλλαξαν.

                Διαθέσιμα εδώ: collection, collectionId, resources, canWrite,
                canManage, fetchAll, openResource. */}
            {state.tab === 'tasks' && (
                <CollectionTasks collectionId={collectionId} collection={collection} canManage={canManage} resources={resources} />
            )}
            {/* ══ ΤΕΛΟΣ P1-4 ═══════════════════════════════════════════════════ */}

            {/* ── ΚΑΡΤΕΛΑ: ΔΡΑΣΤΗΡΙΟΤΗΤΑ ──────────────────────────────────────── */}
            {/* ══ ΣΗΜΕΙΟ ΠΡΟΣΑΡΤΗΣΗΣ P1-7 — ροή δραστηριότητας ══════════════════
                Μία γραμμή, όπως και στις δύο περιοχές παραπάνω.

                Η ροή είναι **ανά συλλογή** (ΛΑ-21), σε αντίθεση με τις ειδοποιήσεις
                που είναι ανά πρόσωπο (ΛΑ-22) και ζουν σε δική τους οθόνη. Δεν
                χρειάζεται `canWrite`: η ροή είναι μόνο αναγνώσιμη και ο έλεγχος
                ορατότητας έγινε ήδη στον εξυπηρετητή, στην ίδια τη διαδρομή.

                **Μετακόμισε σε δική της καρτέλα** με το Σετ Β, και δεν καταργήθηκε:
                σε πλαϊνή στήλη έδειχνε είκοσι ενέργειες σε πλάτος 20rem. */}
            {state.tab === 'activity' && <CollectionActivity collectionId={collectionId} />}
            {/* ══ ΤΕΛΟΣ P1-7 ═══════════════════════════════════════════════════ */}

            {/* ── ΚΑΡΤΕΛΑ: ΜΕΛΗ ───────────────────────────────────────────────── */}
            {state.tab === 'members' && (
                <section className="flex max-w-2xl flex-col gap-3">
                    {/* Το σφάλμα **μέσα στην ενότητα**, δίπλα στην ενέργεια που το
                        προκάλεσε: η άρνηση του τελευταίου συντονιστή έρχεται από τον
                        εξυπηρετητή και πρέπει να φανεί χωρίς κύλιση προς τα πάνω. */}
                    {memberError && (
                        <Notice status="danger" title="Η ενέργεια απέτυχε" onDismiss={dismissMemberError}>
                            {memberError}
                        </Notice>
                    )}

                    <ul className="flex flex-col gap-2">
                        {(collection.members ?? []).map((member) => (
                            <li key={idOf(member)} className="flex items-center gap-2">
                                {/* **Το κοινό συστατικό, όχι χειρόγραφο span.** Εδώ το
                                    όνομα χρήστη γραφόταν σε δικό του `text-sm`, ενώ η
                                    βιβλιοθήκη το δείχνει σε `text-xs` κάτω από το πλήρες
                                    όνομα. Δεν το παρέκαμπτε κάποιο στυλ: το συστατικό
                                    απλώς **δεν χρησιμοποιούνταν**. */}
                                <div className="min-w-0 flex-1">
                                    <UserBadge user={member.user} size="sm" />
                                </div>
                                {/* ΛΑ-04: ο ρόλος γίνεται **επιλέξιμος** για τον
                                    συντονιστή. Ήταν ετικέτα μόνο, δηλαδή λειτουργία που
                                    υπάρχει στο API και δεν φτάνει στον χρήστη — η
                                    κατηγορία Α του Κεφαλαίου 6.

                                    **Ο ίδιος ο συνδεδεμένος εξαιρείται**, με το ίδιο
                                    κριτήριο που ήδη εξαιρεί το κουμπί αφαίρεσης: η
                                    απαίτηση ζητά να ορίζει ο συντονιστής **άλλον**
                                    συντονιστή, και η αυτο-υποβάθμιση αφαιρεί από τον
                                    χρήστη την οθόνη πάνω στην οποία στέκεται. Ο φράχτης
                                    του τελευταίου συντονιστή μένει στον εξυπηρετητή. */}
                                {canManage && idOf(member) !== getUser()?.id ? (
                                    <MemberRoleSelect member={member} onChange={changeMemberRole} />
                                ) : (
                                    <RoleChip role={member.role} />
                                )}
                                {canManage && idOf(member) !== getUser()?.id && (
                                    <MemberRemoveButton member={member} onRemove={removeMember} />
                                )}
                            </li>
                        ))}
                    </ul>

                    {canManage && (
                        <form onSubmit={addMember} className="flex items-end gap-2">
                            <TextField
                                value={newMember}
                                onChange={setNewMember}
                                className="flex-1"
                                aria-label="Όνομα χρήστη νέου μέλους"
                            >
                                <Input placeholder="όνομα χρήστη" />
                            </TextField>
                            <Button type="submit" size="sm">Πρόσκληση</Button>
                        </form>
                    )}
                </section>
            )}

            {/* **Ο ΔΕΥΤΕΡΟΣ ΟΙΚΟΔΕΣΠΟΤΗΣ ΤΟΥ ΔΙΑΛΟΓΟΥ ΠΟΡΟΥ (Β1δ).**

                Ο μόνιμος σύνδεσμος `?resource=` οφείλει να ανοίγει τον πόρο **ακόμη
                κι όταν η ενεργή καρτέλα δεν είναι οι Πόροι** — τότε η λίστα δεν
                αποδίδεται, άρα ούτε η κάρτα που κρατά τον διάλογο.

                Ποιος από τους δύο αναλαμβάνει, το κρίνει **μία** συνάρτηση και όχι
                δύο συνθήκες απόδοσης: και οι δύο τρόποι αποτυχίας είναι σιωπηλοί —
                κανένας οικοδεσπότης δίνει σύνδεσμο που δεν ανοίγει τίποτα, δύο
                δίνουν δύο διαλόγους ο ένας πάνω στον άλλον, και τίποτε από τα δύο
                δεν πετά σφάλμα. */}
            {dialogHost === 'page' && dialogResource && (
                <ResourceItem
                    showCard={false}
                    resource={dialogResource}
                    collectionId={collectionId}
                    canEdit={canWrite}
                    onChanged={fetchAll}
                    onReplace={startAddressReplacement}
                    isOpen
                    onOpenChange={handleLinkedOpenChange}
                    initialPane={landingPane}
                    siblings={resources}
                    knownTags={allTags}
                    forwardedDraft={forwarded?.resourceId === dialogResource.id ? forwarded.values : null}
                    onForwardConsumed={consumeForwarded}
                />
            )}

            {/* ⚠ **Υπό συνθήκη, και είναι διόρθωση ελαττώματος.** Το `isOpen` γίνεται
                ψευδές μετά από **επιτυχή αποθήκευση**, δηλαδή από πηγή εκτός του
                διαλόγου, και το `Modal.Root` του HeroUI κλείνει μόνο όταν το
                κλείσιμο το ξεκινά το ίδιο. Ο κανόνας και η ιστορία του: `ResourceItem`
                και `CollectionTasks`. */}
            {isResourceOpen && (
            <Modal.Root isOpen onOpenChange={setResourceOpen}>
                <Modal.Backdrop>
                    <Modal.Container>
                        {/* Ελάχιστο πλάτος 50vw από τα 640 πίξελ και πάνω: η φόρμα έχει
                            διεύθυνση με κουμπί δίπλα, τίτλο, περιγραφή, εικόνα, ετικέτες
                            και έξι πεδία χρηματοδότησης, και στο προεπιλεγμένο πλάτος
                            έμοιαζε με στήλη. Κάτω από τα 640 μένει το προεπιλεγμένο. */}
                        <Modal.Dialog className="w-full sm:min-w-[50vw]">
                            {/* **Η φόρμα ζει ΜΕΣΑ στο σώμα, ποτέ γύρω από αυτό.**
                                Τυλιγμένη γύρω από κεφαλίδα, σώμα και υποσέλιδο, σπάει την
                                αλυσίδα flex του διαλόγου: το σώμα παύει να είναι άμεσο παιδί
                                του, δεν κληρονομεί ύψος, **δεν κυλάει ποτέ**, και το
                                υποσέλιδο βγαίνει έξω από τον διάλογο που το κόβει — τα
                                κουμπιά υπάρχουν και είναι απρόσιτα.

                                Το κουμπί υποβολής μένει στο υποσέλιδο και δείχνει τη φόρμα
                                με το `form`: η φόρμα δεν χρειάζεται να περιβάλλει το κουμπί
                                για να το ακούει. Μετρήθηκε στον περιηγητή, και με τις δύο
                                διατάξεις. */}
                                <Modal.Header>
                                    <Modal.Heading>
                                        {replacing ? 'Διόρθωση διεύθυνσης' : 'Προσθήκη πόρου'}
                                    </Modal.Heading>
                                    <Modal.CloseTrigger />
                                </Modal.Header>

                                <Modal.Body className="flex flex-col gap-4">
                                    <form id="add-resource-form" onSubmit={createResource} noValidate className="flex flex-col gap-4">
                                    {formError && (
                                        <Notice status="danger" title="Η καταχώρηση απέτυχε">
                                            {formError}
                                            {/* ΛΑ-13: ο σύνδεσμος οδηγεί στον υπάρχοντα πόρο.
                                                Γράφει πλέον την παράμετρο `?resource=` αντί για
                                                άγκιστρο, οπότε δουλεύει και όταν ο υπάρχων πόρος
                                                βρίσκεται σε άλλη σελίδα — που είναι η συνηθισμένη
                                                περίπτωση σε μεγάλη συλλογή, δηλαδή ακριβώς εκεί
                                                όπου το διπλότυπο είναι πιθανό. */}
                                            {duplicateId && (
                                                <>
                                                    {' '}
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setResourceOpen(false);
                                                            openResource(duplicateId);
                                                        }}
                                                        className="font-medium underline underline-offset-4"
                                                    >
                                                        Δείτε τον υπάρχοντα πόρο
                                                    </button>
                                                </>
                                            )}
                                        </Notice>
                                    )}

                                    {/* Απόφαση D12: η ΛΑ-09 δεν διακρίνει σημείο
                                        καταχώρησης, οπότε αντλεί και η φόρμα του πόρταλ
                                        — όχι μόνο η επέκταση. Την άντληση την κάνει ο
                                        **εξυπηρετητής**, γιατί το CORS δεν επιτρέπει
                                        στον πελάτη να διαβάσει ξένη προέλευση. */}
                                    <FieldWithAction
                                        message={urlMessage}
                                        action={(
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="shrink-0"
                                                isPending={isFetchingMeta}
                                                onPress={fetchMetadata}
                                            >
                                                <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />
                                                {isFetchingMeta ? P1_5_TEXT.fetchingMetadata : P1_5_TEXT.fetchMetadata}
                                            </Button>
                                        )}
                                    >
                                        {({ describedBy }) => (
                                            <TextField
                                                value={form.url}
                                                onChange={set('url')}
                                                onBlur={validation.touch('url')}
                                                isInvalid={Boolean(urlMessage)}
                                                aria-describedby={describedBy}
                                                autoFocus
                                            >
                                                <Label>Διεύθυνση</Label>
                                                <Input placeholder="https://..." />
                                            </TextField>
                                        )}
                                    </FieldWithAction>

                                    {metaNotice && (
                                        <Notice
                                            status={metaNotice.status}
                                            title={metaNotice.title}
                                            onDismiss={() => setMetaNotice(null)}
                                        >
                                            {metaNotice.body}
                                        </Notice>
                                    )}

                                    <TextField
                                        value={form.title}
                                        onChange={set('title')}
                                        onBlur={validation.touch('title')}
                                        isInvalid={Boolean(titleMessage)}
                                    >
                                        <Label>
                                            Τίτλος
                                            <SourceHint source={metaSources?.title} />
                                        </Label>
                                        <Input />
                                        <FieldError>{titleMessage}</FieldError>
                                    </TextField>

                                    <TextField value={form.description} onChange={set('description')}>
                                        <Label>
                                            Περιγραφή
                                            <SourceHint source={metaSources?.description} />
                                        </Label>
                                        <TextArea rows={3} />
                                    </TextField>

                                    <TextField
                                        value={form.image}
                                        onChange={set('image')}
                                        isInvalid={Boolean(imageMessage)}
                                    >
                                        <Label>
                                            Διεύθυνση εικόνας
                                            <SourceHint source={metaSources?.image} />
                                        </Label>
                                        <Input placeholder="https://..." />
                                        <FieldError>{imageMessage}</FieldError>
                                    </TextField>

                                    <TextField value={form.tags} onChange={set('tags')}>
                                        <Label>Ετικέτες</Label>
                                        <Input placeholder="π.χ. horizon, προθεσμία 2026" />
                                        <span className="text-sm text-muted">Χωρίστε τις με κόμματα.</span>
                                    </TextField>

                                    {/* D49: **οι ίδιες** προτάσεις με το πεδίο της
                                        επεξεργασίας, από **τα ίδια** δεδομένα — τις
                                        ετικέτες που η κάρτα «Ετικέτες» έχει ήδη στη
                                        μνήμη της. Το πεδίο εδώ κρατά **κείμενο με
                                        κόμματα** και όχι πίνακα, οπότε η προσθήκη
                                        γράφει στο κείμενο· η περιοχή προτάσεων δεν
                                        γνωρίζει τη διαφορά, γιατί δεν κρατά κατάσταση. */}
                                    <SuggestionsField
                                        suggestions={allTags}
                                        value={formTags}
                                        onAdd={addFormTag}
                                        label="Ετικέτες της συλλογής"
                                    />

                                    <FundingFields value={form.funding} onChange={set('funding')} />
                                    </form>
                                </Modal.Body>

                                <Modal.Footer>
                                    <Button variant="outline" type="button" onPress={() => setResourceOpen(false)}>
                                        Ακύρωση
                                    </Button>
                                    <Button type="submit" form="add-resource-form" isPending={isSaving}>Προσθήκη</Button>
                                </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal.Root>
            )}

            {/* ΛΑ-05: η επεξεργασία της συλλογής. Τα τέσσερα πεδία και οι κατάλογοί
                τους είναι **τα ίδια** με τη δημιουργία, γιατί η απαίτηση ονομάζει τα
                ίδια τέσσερα στοιχεία και για τις δύο ενέργειες. */}
            {isEditOpen && editForm && (
            <Modal.Root isOpen onOpenChange={setEditOpen}>
                <Modal.Backdrop>
                    <Modal.Container>
                        <Modal.Dialog>
                            <Modal.Header>
                                <Modal.Heading>Επεξεργασία συλλογής</Modal.Heading>
                                <Modal.CloseTrigger />
                            </Modal.Header>

                            {/* Η φόρμα ζει ΜΕΣΑ στο σώμα, ποτέ γύρω από αυτό: τυλιγμένη
                                γύρω από κεφαλίδα, σώμα και υποσέλιδο σπάει την αλυσίδα
                                flex του διαλόγου και η κύλιση παύει να λειτουργεί. */}
                            <Modal.Body className="flex flex-col gap-4">
                                <form id="edit-collection-form" onSubmit={saveCollection} noValidate className="flex flex-col gap-4">
                                    {/* Το σφάλμα **μέσα** στο σώμα, πάνω από τα πεδία που
                                        αφορά: το υποσέλιδο έχει δική του θέση και το σώμα
                                        δική του κύλιση, οπότε μήνυμα έξω από εδώ μπορεί να
                                        βρεθεί εκτός οπτικού πεδίου. */}
                                    {editError && <Notice status="danger" title="Η αποθήκευση απέτυχε">{editError}</Notice>}

                                    <TextField
                                        name="title"
                                        value={editForm.title}
                                        onChange={(value) => setEditForm((current) => ({ ...current, title: value }))}
                                        onBlur={editValidation.touch('title')}
                                        isInvalid={Boolean(editTitleMessage)}
                                        autoFocus
                                    >
                                        <Label>Τίτλος</Label>
                                        <Input />
                                        <FieldError>{editTitleMessage}</FieldError>
                                    </TextField>

                                    {/* **D47, ΚΑΙ ΕΙΝΑΙ Η ΔΕΥΤΕΡΗ ΦΟΡΜΑ ΤΟΥ ΙΔΙΟΥ ΠΕΔΙΟΥ.**
                                        Το λεξιλόγιο ενημέρωσε τη φόρμα **δημιουργίας** και
                                        άφησε εδώ πεδίο ελεύθερου κειμένου: η οθόνη
                                        προσκαλούσε τον χρήστη να πληκτρολογήσει και ο
                                        διακομιστής απέρριπτε με **422** ό,τι έγραφε. Η
                                        άρνηση ήταν σωστή· η **πρόσκληση** ήταν το σφάλμα.
                                        *Πεδίο που δέχεται πληκτρολόγηση υπόσχεται ότι η
                                        πληκτρολόγηση γίνεται δεκτή.* */}
                                    <TextField name="topic" value={editForm.topic} isDisabled>
                                        <Label>{TOPICS.picker}</Label>
                                        <Input />
                                    </TextField>

                                    {topicNames.length === 0 ? (
                                        <p className="text-sm text-muted">
                                            {TOPICS.pickerEmpty}{' '}
                                            <Link to="/topics" className="font-medium underline">
                                                {TOPICS.pickerEmptyLink}
                                            </Link>
                                        </p>
                                    ) : (
                                        <div className="flex flex-col gap-2">
                                            <SuggestionsField
                                                suggestions={topicNames}
                                                value={editForm.topic ? [editForm.topic] : []}
                                                onAdd={(value) => setEditForm((current) => ({ ...current, topic: value }))}
                                                multiple={false}
                                                allowFree={false}
                                                label={TOPICS.picker}
                                                hint={TOPICS.pickerHint}
                                            />
                                            {editForm.topic && (
                                                <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="outline"
                                                    className="self-start"
                                                    onPress={() => setEditForm((current) => ({ ...current, topic: '' }))}
                                                >
                                                    {TOPICS.clear}
                                                </Button>
                                            )}
                                        </div>
                                    )}

                                    <TextField
                                        name="description"
                                        value={editForm.description}
                                        onChange={(value) => setEditForm((current) => ({ ...current, description: value }))}
                                    >
                                        <Label>Περιγραφή</Label>
                                        <TextArea rows={3} />
                                    </TextField>

                                    {/* Ο καθορισμός ορατότητας είναι **ξεχωριστή γραμμή** του
                                        Πίνακα 1 (`collection:visibility`) και ελέγχεται ρητά
                                        στον εξυπηρετητή, χωριστά από το `collection:manage`.
                                        Η οθόνη δεν επαναλαμβάνει τον έλεγχο — δεν θα είχε πώς:
                                        οι δύο ενέργειες απαιτούν την ίδια βαθμίδα και ο
                                        πελάτης δεν τις ξεχωρίζει. Ο φράχτης είναι το API. */}
                                    <Select placeholder={SELECT_PLACEHOLDER}
                                        selectedKey={editForm.visibility}
                                        onSelectionChange={(key) => setEditForm((current) => ({ ...current, visibility: String(key) }))}
                                    >
                                        <Label>Επίπεδο πρόσβασης</Label>
                                        <Select.Trigger>
                                            <Select.Value />
                                            <Select.Indicator />
                                        </Select.Trigger>
                                        <Select.Popover>
                                            <ListBox items={VISIBILITIES}>
                                                {(item) => <ListBoxItem id={item.id}>{item.label}</ListBoxItem>}
                                            </ListBox>
                                        </Select.Popover>
                                    </Select>

                                    {/* ΜΛΑ-03: η μετάβαση σε ιδιωτική **αποσύρει** πρόσβαση
                                        που ήδη ασκείται από τρίτους, και αυτό δεν φαίνεται
                                        από πουθενά αλλού στην οθόνη. Δεν προειδοποιεί για
                                        απώλεια δεδομένων: τίποτα δεν διαγράφεται, και η
                                        ενέργεια αναιρείται. */}
                                    {collection.visibility === 'public' && editForm.visibility === 'private' && (
                                        <p className="text-sm text-muted">
                                            Η συλλογή θα πάψει να εμφανίζεται στις δημόσιες, οι εξωτερικές
                                            ειδοποιήσεις προς τρίτα συστήματα θα σταματήσουν, και το κλειδί
                                            πρόσβασης που δείχνει σε αυτήν δεν θα διαβάζει πλέον. Η ενέργεια
                                            αναιρείται επαναφέροντας το επίπεδο πρόσβασης.
                                        </p>
                                    )}
                                </form>
                            </Modal.Body>

                            <Modal.Footer>
                                <Button variant="outline" type="button" onPress={() => setEditOpen(false)}>Ακύρωση</Button>
                                <Button type="submit" form="edit-collection-form" isPending={isSavingEdit}>Αποθήκευση</Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal.Root>
            )}

            {/* ⚠ **Υπό συνθήκη**, για τον ίδιο λόγο με τον διάλογο πόρου παραπάνω: το
                `isDeleteOpen` γίνεται ψευδές από τον **χειριστή σφάλματος** της
                διαγραφής, δηλαδή από πηγή που δεν είναι το κλείσιμο του διαλόγου. */}
            {isDeleteOpen && (
            <Modal.Root isOpen onOpenChange={setDeleteOpen}>
                <Modal.Backdrop>
                    <Modal.Container size="sm">
                        <Modal.Dialog>
                            <Modal.Header>
                                <Modal.Heading>{COLLECTION_REMOVAL.title}</Modal.Heading>
                                <Modal.CloseTrigger />
                            </Modal.Header>
                            <Modal.Body className="flex flex-col gap-3">
                                <p>Η διαγραφή της «{collection.title}» δεν αναστρέφεται.</p>

                                {/* ΜΛΑ-03: **τα πλήθη, μετρημένα τώρα.** Τρεις καταστάσεις,
                                    και καμία δεν καταρρέει στην άλλη. */}
                                {removalFailed ? (
                                    <Notice status="warning" title={COLLECTION_REMOVAL.countingFailed}>
                                        {COLLECTION_REMOVAL.irreversible}
                                    </Notice>
                                ) : removal === null ? (
                                    <p className="text-sm text-muted" aria-busy="true">
                                        {COLLECTION_REMOVAL.counting}
                                    </p>
                                ) : (
                                    <Notice status="danger" title={COLLECTION_REMOVAL.irreversible}>
                                        {COLLECTION_REMOVAL.summaryOf(removal).length > 0 ? (
                                            <>
                                                {COLLECTION_REMOVAL.summaryOf(removal).map((line) => (
                                                    <p key={line} className="font-semibold text-foreground">{line}</p>
                                                ))}
                                                {/* Μόνο όταν **υπάρχουν** πόροι που επιβιώνουν: αλλιώς η
                                                    γραμμή απαντά σε απορία που δεν γεννήθηκε. */}
                                                {(meta?.total ?? 0) > removal.resources && (
                                                    <p className="mt-1 text-sm">{COLLECTION_REMOVAL.sharedNote}</p>
                                                )}
                                            </>
                                        ) : (
                                            <p>{COLLECTION_REMOVAL.nothingLost}</p>
                                        )}
                                    </Notice>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="outline" onPress={() => setDeleteOpen(false)}>
                                    {COLLECTION_REMOVAL.cancel}
                                </Button>
                                {/* **Ανενεργό όσο μετράει.** Επιβεβαίωση που ζητείται πριν
                                    φανεί το κόστος είναι επιβεβαίωση χωρίς πληροφορία — και
                                    ο χρήστης που πατά γρήγορα δεν θα δει ποτέ τους αριθμούς
                                    που η ίδια η ΜΛΑ-03 απαιτεί. Μετά από **αποτυχία** της
                                    μέτρησης το κουμπί ενεργοποιείται: η αδυναμία να μετρηθεί
                                    το κόστος δεν είναι λόγος να απαγορευτεί η ενέργεια, αρκεί
                                    να ειπωθεί ότι δεν μετρήθηκε. */}
                                <Button
                                    variant="danger"
                                    onPress={deleteCollection}
                                    isDisabled={removal === null && !removalFailed}
                                    isPending={isDeleting}
                                >
                                    {COLLECTION_REMOVAL.confirm}
                                </Button>
                            </Modal.Footer>
                        </Modal.Dialog>
                    </Modal.Container>
                </Modal.Backdrop>
            </Modal.Root>
            )}

            {/* ⚠ **ΤΟ ΑΙΩΡΟΥΜΕΝΟ ΚΟΥΜΠΙ ΚΑΤΑΧΩΡΗΣΗΣ (ΛΑ-08).**
                Η κύρια ενέργεια της σελίδας, στην κάτω δεξιά γωνία, με το πρότυπο
                του Material. Τέσσερις αποφάσεις, και καμία δεν είναι αισθητική:

                1. **Κρατά το κείμενό του σε πλατιά οθόνη και το κρύβει σε στενή.**
                   Το σκέτο «+» απαιτεί από τον χρήστη να μαντέψει τι προσθέτει —
                   και η σελίδα έχει τρία πράγματα που προστίθενται (πόροι, μέλη,
                   καθήκοντα). Το `aria-label` λέει την ολόκληρη φράση **και στις
                   δύο** περιπτώσεις, οπότε ο αναγνώστης οθόνης δεν χάνει ποτέ το
                   αντικείμενο.

                2. **Κάθεται ψηλότερα από το «επιστροφή στην κορυφή»** και όχι στο
                   ίδιο σημείο: δύο αιωρούμενα κουμπιά στην ίδια γωνία σημαίνουν
                   ότι το ένα σκεπάζει το άλλο, και εκείνο που εμφανίζεται δεύτερο
                   κερδίζει χωρίς κανείς να το έχει αποφασίσει.

                3. **Αποδίδεται μόνο στην καρτέλα των πόρων.** Ενέργεια που
                   αιωρείται πάνω από τα καθήκοντα ή τα μέλη προσθέτει πόρο σε
                   οθόνη που δεν δείχνει πόρους — κουμπί που κάνει κάτι αλλού.

                4. **ΛΑ-07:** σε αρχειοθετημένη συλλογή το `canWrite` είναι ψευδές
                   και το κουμπί δεν αποδίδεται καθόλου. */}
            {canWrite && state.tab === 'resources' && (
                /* ⚠ **ΜΟΝΟ ΤΟ «+», ΚΑΙ ΜΕΓΑΛΥΤΕΡΟ ΣΕ ΥΠΟΛΟΓΙΣΤΗ.** Η πρώτη μορφή
                    κρατούσε το λεκτικό σε πλατιά οθόνη, με επιχείρημα ότι το σκέτο
                    «+» ζητά από τον χρήστη να μαντέψει τι προστίθεται. Το
                    επιχείρημα **δεν χάνεται**: το λεκτικό υπάρχει ολόκληρο στο
                    `aria-label` και στο `title`, δηλαδή σε **κάθε** πλάτος και για
                    κάθε τρόπο πρόσβασης.

                    Ό,τι χάνεται είναι η **οριζόντια μάζα**: ένα κουμπί με κείμενο
                    που αιωρείται πάνω από το πλέγμα καλύπτει μέρος μιας κάρτας
                    μονίμως. Ο κύκλος καλύπτει λιγότερο, και το μεγαλύτερο μέγεθος
                    σε υπολογιστή κρατά τον στόχο ευμέγεθη — 56 πίξελ, το ίδιο
                    μέτρο που ορίζει το Material για το κύριο αιωρούμενο κουμπί. */
                <Button
                    onPress={openResourceForm}
                    aria-label="Προσθήκη πόρου"
                    title="Προσθήκη πόρου"
                    className="fixed right-4 bottom-20 z-20 flex h-12 w-12 items-center justify-center rounded-full p-0 text-2xl shadow-lg sm:right-6 sm:bottom-24 sm:h-13 sm:w-13"
                >
                    <i className="fa-solid fa-plus" aria-hidden="true" />
                </Button>
            )}
        </div>
    );
}
